import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingCart, Plus, User, X, ChevronLeft, ChevronRight, MoreVertical } from "lucide-react";
import { formatDOP } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

/**
 * Barra horizontal compacta de carritos en el tope del POS.
 * Iter 53 — reemplaza el panel lateral CartListPanel para recuperar ancho útil.
 */
export default function CartTabsBar({ activeCartId, onSelectCart, onNewCart, refreshTrigger }) {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(false);
  const { can } = useAuth();
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const load = useCallback(async () => {
    if (!can("pos.edit_any_cart")) { setCarts([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/pos/carts?status=abierto");
      setCarts(data);
    } catch (_) { /* silent poll */ } finally { setLoading(false); }
  }, [can]);

  useEffect(() => { load(); }, [load, refreshTrigger]);
  useEffect(() => { const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  // Update scroll indicators
  const updateScrollIndicators = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollIndicators();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollIndicators);
    const ro = new ResizeObserver(updateScrollIndicators);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", updateScrollIndicators); ro.disconnect(); };
  }, [carts, updateScrollIndicators]);

  const scrollBy = (delta) => {
    scrollerRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  };

  const discard = async (id, number) => {
    if (!window.confirm(`¿Descartar el carrito ${number}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/pos/carts/${id}`);
      toast.success(`Carrito ${number} descartado`);
      load();
      if (activeCartId === id) onSelectCart(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="bg-card border rounded-md flex items-stretch gap-1 h-11 px-1 relative" data-testid="cart-tabs-bar">
      {/* Icono carritos + contador */}
      <div className="flex items-center gap-1.5 px-2 border-r border-border/70 text-xs shrink-0">
        <ShoppingCart className="w-4 h-4 text-primary" />
        <span className="font-semibold hidden md:inline">Carritos</span>
        <span className="text-muted-foreground">({carts.length})</span>
      </div>

      {/* Botón scroll izq */}
      {canScrollLeft && (
        <button
          onClick={() => scrollBy(-200)}
          className="absolute left-24 top-1/2 -translate-y-1/2 z-10 bg-background/95 backdrop-blur border rounded-full w-6 h-6 flex items-center justify-center shadow"
          data-testid="cart-tabs-scroll-left"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Tabs scrollables */}
      <div
        ref={scrollerRef}
        className="flex items-center gap-1 overflow-x-auto scrollbar-none flex-1 min-w-0"
        style={{ scrollbarWidth: "none" }}
      >
        {carts.length === 0 && (
          <span className="text-xs text-muted-foreground px-2 whitespace-nowrap">Sin carritos abiertos · usa "+" para crear uno</span>
        )}
        {carts.map((c) => {
          const isActive = c.id === activeCartId;
          const itemCount = (c.items || []).length;
          const customerShort = (c.customer_name || "Sin cliente").length > 18
            ? (c.customer_name || "").slice(0, 18) + "…"
            : (c.customer_name || "Sin cliente");
          return (
            <div
              key={c.id}
              onClick={() => onSelectCart(c.id)}
              className={`shrink-0 flex items-center gap-1.5 h-9 rounded-md border cursor-pointer transition-colors text-xs px-2 ${
                isActive
                  ? "bg-primary/10 border-primary text-primary"
                  : "bg-background hover:bg-accent border-transparent"
              }`}
              data-testid={`cart-tab-${c.number}`}
              title={`${c.customer_name || "Sin cliente"} · ${itemCount} item${itemCount !== 1 ? "s" : ""}`}
            >
              <ShoppingCart className="w-3.5 h-3.5 shrink-0" />
              <span className="font-bold">{c.number.replace("CAR-", "#")}</span>
              <span className="opacity-70 hidden lg:inline max-w-[10rem] truncate">· {customerShort}</span>
              <span className="font-mono text-[10px] opacity-80 hidden xl:inline">{formatDOP(c.totals?.total || 0)}</span>
              {itemCount > 0 && (
                <span className={`text-[10px] font-black px-1 rounded ${isActive ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {itemCount}
                </span>
              )}
              {isActive && can("pos.discard_cart") && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <button className="ml-0.5 hover:bg-background/50 rounded p-0.5" data-testid={`cart-tab-menu-${c.number}`}>
                      <MoreVertical className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={(e) => { e.stopPropagation(); discard(c.id, c.number); }}
                      data-testid={`cart-tab-discard-${c.number}`}
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Descartar carrito
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })}
      </div>

      {/* Botón scroll der */}
      {canScrollRight && (
        <button
          onClick={() => scrollBy(200)}
          className="absolute right-20 top-1/2 -translate-y-1/2 z-10 bg-background/95 backdrop-blur border rounded-full w-6 h-6 flex items-center justify-center shadow"
          data-testid="cart-tabs-scroll-right"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Botón nuevo */}
      <button
        onClick={onNewCart}
        disabled={!can("pos.create_cart")}
        className="shrink-0 h-9 my-auto px-3 flex items-center gap-1 text-xs font-bold bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-40"
        data-testid="new-cart-btn"
      >
        <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nuevo</span>
      </button>
    </div>
  );
}
